// TTS playback in the page (webview or browser), and a virtual clock for sessions whose audio
// plays elsewhere. Both expose the playback position for word-by-word highlighting.

import type { AudioHub } from "./audio-hub";
import { scheduleCue, type CueKind } from "./earcons";
import { int16ToFloat32, TTS_SAMPLE_RATE, type Id } from "./protocol";
import { PlaybackScheduler, type Position } from "./scheduler";

export interface Player {
  /** Audio of a reply (PCM16 24 kHz). Silenced or superseded replies are dropped. */
  enqueue(replyId: Id, pcm: Int16Array): void;
  /** The reply was silenced: stop it now and drop anything that still arrives for it. */
  interrupt(replyId: Id): void;
  /**
   * Pause the reply being played where it is (protocol 1.9; `replyId`: only if it is that one).
   * Not an interruption: its place and its audio are kept, and audio that still arrives for it
   * is kept too. Returns where it paused, or `null` if nothing was paused.
   */
  pause?(replyId?: Id): Position | null;
  /** Carry on from the pause. Returns false if nothing was paused. */
  resume?(replyId?: Id): boolean;
  /** The paused reply, if any. */
  pausedReply?(): Id | null;
  stopAll(): void;
  /** A new kit process (its ids start over). */
  reset(): void;
  /** Where playback is right now. */
  progressNow(): Position | null;
  onProgress(cb: (replyId: Id, ms: number) => void): void;
  onDone(cb: (replyId: Id, ms: number) => void): void;
  /** Output level 0..1, for the orb. */
  level(): number;
  /** iOS: call inside the tap that starts a session. */
  unlock?(): void;
  stats?(): Record<string, unknown>;
  /**
   * 1.10: where a clip of ignored speech plays (call it inside the tap): this player's own
   * running output, so no second audio context is made (iOS) and the echo canceller hears it.
   */
  clipOutput?(): { ctx: AudioContext; destination: AudioNode };
}

/**
 * Plays nothing: follows the kit's own playback (PC speakers, or the virtual clock of a text
 * session) by timing `tts.chunk`s as they arrive. Audio starts as soon as a chunk is
 * synthesized, so the estimate is within the output device's buffer of the truth; the kit's
 * `reply.heard` settles it.
 */
export class ClockPlayer implements Player {
  private readonly scheduler = new PlaybackScheduler(0);
  private progressCb: (g: Id, ms: number) => void = () => {};
  private doneCb: (g: Id, ms: number) => void = () => {};

  constructor(private readonly now: () => number = () => performance.now() / 1000) {}

  /** A `tts.chunk` of `ms` was handed to the kit's player. */
  chunk(replyId: Id, ms: number): void {
    this.scheduler.enqueue(replyId, ms / 1000, this.now());
  }

  enqueue(replyId: Id, pcm: Int16Array): void {
    this.chunk(replyId, (pcm.length / TTS_SAMPLE_RATE) * 1000);
  }

  interrupt(replyId: Id): void {
    this.scheduler.interrupt(replyId);
  }

  pause(replyId?: Id): Position | null {
    const p = this.scheduler.pause(this.now(), replyId);
    return p && { replyId: p.replyId, msPlayed: Math.round(p.playedS * 1000) };
  }

  resume(replyId?: Id): boolean {
    return !!this.scheduler.resume(this.now(), replyId);
  }

  pausedReply(): Id | null {
    return this.scheduler.pausedReply;
  }

  stopAll(): void {
    this.scheduler.stopAll();
  }

  reset(): void {
    this.scheduler.reset();
  }

  progressNow(): Position | null {
    return this.scheduler.progress(this.now());
  }

  /** Call periodically (the client does, every 100 ms) to fire progress and done callbacks. */
  tick(): void {
    const now = this.now();
    const done = this.scheduler.pollDone(now);
    if (done) return this.doneCb(done.replyId, done.msPlayed);
    const p = this.scheduler.progress(now);
    if (p && this.scheduler.isPlaying(now)) this.progressCb(p.replyId, p.msPlayed);
  }

  onProgress(cb: (g: Id, ms: number) => void): void {
    this.progressCb = cb;
  }

  onDone(cb: (g: Id, ms: number) => void): void {
    this.doneCb = cb;
  }

  level(): number {
    return this.scheduler.isPlaying(this.now()) ? 0.45 + 0.15 * Math.sin(this.now() * 9) : 0;
  }
}

/**
 * How TTS audio reaches the speakers.
 * - "direct": AudioContext.destination.
 * - "loopback": through a local RTCPeerConnection pair into an <audio> element. Chromium's echo
 *   canceller uses WebRTC playout as its reference, so this is the fallback if "direct"
 *   playback leaks into the mic.
 */
export type OutputMode = "direct" | "loopback";

export interface WebAudioPlayerOptions {
  /**
   * The AudioContext to share with the mic (./audio-hub.ts): one per page, as iOS wants. When
   * the hub replaces it, a reply being played carries on in the new one from where it was.
   * Without it the player makes its own context (as before).
   */
  hub?: AudioHub;
}

/** Plays a reply's audio in the page and reports progress (ported from live-voice-ai). */
export class WebAudioPlayer implements Player {
  private ctx!: AudioContext;
  private readonly scheduler = new PlaybackScheduler();
  private analyser!: AnalyserNode;
  private readonly nodes = new Map<number, AudioBufferSourceNode>();
  private progressCb: (g: Id, ms: number) => void = () => {};
  private doneCb: (g: Id, ms: number) => void = () => {};
  private readonly levelBuf = new Float32Array(512);
  private loopback: Loopback | null = null;
  private wasHidden = false;
  /** The current reply's audio as it arrived, kept to carry on after a pause (protocol 1.9). */
  private kept: { replyId: Id; pcm: Int16Array[] } | null = null;
  private readonly timer: ReturnType<typeof setInterval>;
  private readonly hub: AudioHub | null;
  /** Times a reply was carried over to a new context (the hub replaced it mid-reply). */
  private moves = 0;

  constructor(private readonly mode: OutputMode = "direct", private readonly sinkId?: string, opts: WebAudioPlayerOptions = {}) {
    this.hub = opts.hub ?? null;
    if (this.hub) {
      const hub = this.hub;
      this.build(hub.context());
      hub.onSwap((next, prev) => this.moveTo(next, prev));
      // iOS can suspend the context (e.g. when the mic starts): get it running again.
      hub.onState((ctx) => {
        if (ctx === this.ctx && ctx.state !== "running" && ctx.state !== "closed") void ctx.resume().catch(() => {});
      });
    } else this.build();
    // After iOS backgrounds the page its audio output can stay dead; rebuild it on the next tap
    // (with a hub, the hub decides: the mic marks it for a fresh context on the next tap).
    if (typeof document !== "undefined" && !this.hub) {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") this.wasHidden = true;
      });
    }
    this.timer = setInterval(() => this.tick(), 100);
  }

  private build(shared?: AudioContext): void {
    this.ctx = shared ?? new AudioContext({ latencyHint: "interactive" });
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 512;
    if (this.mode === "loopback") {
      const dest = this.ctx.createMediaStreamDestination();
      this.analyser.connect(dest);
      this.loopback = new Loopback(dest.stream, this.sinkId);
    } else {
      this.analyser.connect(this.ctx.destination);
      if (this.sinkId) void (this.ctx as AudioContext & { setSinkId?: (id: string) => Promise<void> }).setSinkId?.(this.sinkId);
    }
    if (shared) return; // the hub owns its state handler
    // iOS can suspend or interrupt the context (e.g. when the mic starts): get it running again.
    this.ctx.onstatechange = () => {
      // After a call iOS can keep an interrupted context deaf: the next tap rebuilds it.
      if ((this.ctx.state as string) === "interrupted") this.wasHidden = true;
      if (this.ctx.state !== "running" && this.ctx.state !== "closed") void this.ctx.resume();
    };
  }

  /** iOS took the audio away (a call): the output context is "interrupted". */
  interrupted(): boolean {
    return (this.ctx.state as string) === "interrupted";
  }

  stats(): Record<string, unknown> {
    return { playerCtx: this.ctx.state, playerTime: Math.round(this.ctx.currentTime * 10) / 10, queued: this.nodes.size, sharedCtx: !!this.hub, playerMoves: this.moves };
  }

  /**
   * The hub replaced the context: rebuild the output on the new one. A reply being played is
   * paused at the old clock and carried on in the new one from the same place; queued timing
   * moves to the new clock.
   */
  private moveTo(next: AudioContext, prev: AudioContext | null): void {
    if (next === this.ctx) return;
    const oldNow = prev && prev.state !== "closed" ? prev.currentTime : this.ctx.currentTime;
    const carry = this.scheduler.isPlaying(oldNow) ? this.scheduler.pause(oldNow) : null;
    [...this.nodes.keys()].forEach((id) => this.stopNode(id));
    this.loopback?.close();
    this.loopback = null;
    try {
      this.analyser.disconnect();
    } catch {
      /* already gone with its context */
    }
    this.build(next);
    this.scheduler.rebase(next.currentTime - oldNow);
    if (!carry) return;
    this.moves++;
    const r = this.scheduler.resume(next.currentTime);
    if (r) this.play(r.seg, r.startAt, restOf(this.kept?.pcm ?? [], Math.round(r.fromS * TTS_SAMPLE_RATE)));
  }

  /** New kit process: forget old replies; after the page was backgrounded, rebuild the output. */
  reset(): void {
    this.scheduler.reset().forEach((id) => this.stopNode(id));
    this.kept = null;
    if (this.wasHidden || this.ctx.state === "closed") this.rebuild();
  }

  /** A fresh output (iOS can leave one dead after the background or a call). */
  private rebuild(): void {
    this.wasHidden = false;
    if (this.hub) {
      this.hub.rebuild("player"); // moveTo carries the reply over
      return;
    }
    this.nodes.forEach((_, id) => this.stopNode(id));
    this.loopback?.close();
    this.loopback = null;
    void this.ctx.close().catch(() => {});
    this.build();
  }

  /** iOS only starts audio from a user gesture: call this from the tap that starts a session. */
  unlock(): void {
    if (this.hub) {
      // Fresh in this tap if the hub was told to (after a call, the background): moveTo follows.
      this.hub.prepare();
      return this.tick0();
    }
    // A paused reply keeps its audio: only the output is rebuilt (it carries on there).
    if (this.wasHidden || this.ctx.state === "closed") {
      if (this.scheduler.pausedReply !== null) this.rebuild();
      else this.reset();
    }
    void this.ctx.resume();
    this.tick0();
  }

  /** One silent sample, inside the tap: what unlocks output on iOS. */
  private tick0(): void {
    const silent = this.ctx.createBufferSource();
    silent.buffer = this.ctx.createBuffer(1, 1, this.ctx.sampleRate);
    silent.connect(this.ctx.destination);
    silent.start();
  }

  /**
   * 1.10: the output a clip of ignored speech plays through (./mine.ts `ClipPlayer`): this
   * player's own context, woken inside the tap like `unlock`, and the node replies go through
   * (so the echo canceller hears it as far-end audio). Never a second context.
   */
  clipOutput(): { ctx: AudioContext; destination: AudioNode } {
    if (this.hub) {
      this.hub.prepare();
      return { ctx: this.ctx, destination: this.analyser };
    }
    if (this.wasHidden || this.ctx.state === "closed") {
      if (this.scheduler.pausedReply !== null) this.rebuild();
      else this.reset();
    }
    void this.ctx.resume();
    return { ctx: this.ctx, destination: this.analyser };
  }

  /**
   * Plays a short earcon (./earcons.ts) through the same output as replies, so the echo
   * canceller sees it as far-end audio. Only on a running context: iOS plays nothing without a
   * gesture, so a context that isn't running (backgrounded, not yet unlocked) plays no cue
   * rather than one queued up for later. Returns the cue's length in ms, or 0 if not played.
   */
  cue(kind: CueKind, volume?: number): number {
    if (this.ctx.state !== "running") return 0;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return 0;
    return scheduleCue(this.ctx, this.analyser, kind, volume);
  }

  progressNow(): Position | null {
    return this.scheduler.progress(this.ctx.currentTime);
  }

  onProgress(cb: (g: Id, ms: number) => void): void {
    this.progressCb = cb;
  }

  onDone(cb: (g: Id, ms: number) => void): void {
    this.doneCb = cb;
  }

  enqueue(replyId: Id, pcm: Int16Array): void {
    if (this.ctx.state === "suspended") void this.ctx.resume();
    const slot = this.scheduler.enqueue(replyId, pcm.length / TTS_SAMPLE_RATE, this.ctx.currentTime);
    if (!slot) return;
    if (this.kept?.replyId !== replyId) this.kept = { replyId, pcm: [] };
    this.kept.pcm.push(pcm);
    slot.superseded.forEach((id) => this.stopNode(id));
    if (slot.deferred) return; // paused: it plays on resume
    this.play(slot.seg, slot.startAt, pcm);
  }

  private play(seg: number, startAt: number, pcm: Int16Array): void {
    if (!pcm.length) return;
    const buffer = this.ctx.createBuffer(1, pcm.length, TTS_SAMPLE_RATE);
    buffer.copyToChannel(int16ToFloat32(pcm), 0);
    const node = this.ctx.createBufferSource();
    node.buffer = buffer;
    node.connect(this.analyser);
    node.onended = () => this.nodes.delete(seg);
    node.start(startAt);
    this.nodes.set(seg, node);
  }

  interrupt(replyId: Id): void {
    this.scheduler.interrupt(replyId).forEach((id) => this.stopNode(id));
    if (this.kept?.replyId === replyId) this.kept = null;
  }

  pause(replyId?: Id): Position | null {
    const p = this.scheduler.pause(this.ctx.currentTime, replyId);
    if (!p) return null;
    p.stop.forEach((id) => this.stopNode(id));
    return { replyId: p.replyId, msPlayed: Math.round(p.playedS * 1000) };
  }

  resume(replyId?: Id): boolean {
    if (this.scheduler.pausedReply === null || (replyId !== undefined && replyId !== this.scheduler.pausedReply)) return false;
    // Held before any of its audio came: released, nothing to play yet.
    if (this.scheduler.pausedReply !== this.scheduler.currentReply) {
      this.scheduler.resume(this.ctx.currentTime, replyId);
      return true;
    }
    // After a call or the background the output may be dead: carry on in a fresh one.
    if (this.ctx.state === "closed") this.rebuild();
    if (this.ctx.state !== "running") void this.ctx.resume();
    const r = this.scheduler.resume(this.ctx.currentTime, replyId);
    if (!r) return false;
    this.play(r.seg, r.startAt, restOf(this.kept?.pcm ?? [], Math.round(r.fromS * TTS_SAMPLE_RATE)));
    return true;
  }

  pausedReply(): Id | null {
    return this.scheduler.pausedReply;
  }

  stopAll(): void {
    this.scheduler.stopAll().forEach((id) => this.stopNode(id));
    this.kept = null;
  }

  level(): number {
    this.analyser.getFloatTimeDomainData(this.levelBuf);
    let s = 0;
    for (const v of this.levelBuf) s += v * v;
    const rms = Math.sqrt(s / this.levelBuf.length);
    return Math.min(1, Math.max(0, (20 * Math.log10(rms + 1e-6) + 50) / 40));
  }

  close(): void {
    clearInterval(this.timer);
    this.loopback?.close();
    if (!this.hub) void this.ctx.close().catch(() => {}); // a shared context is the hub's
  }

  private tick(): void {
    const now = this.ctx.currentTime;
    const done = this.scheduler.pollDone(now);
    if (done) return this.doneCb(done.replyId, done.msPlayed);
    const p = this.scheduler.progress(now);
    if (p && this.scheduler.isPlaying(now)) this.progressCb(p.replyId, p.msPlayed);
  }

  private stopNode(id: number): void {
    const node = this.nodes.get(id);
    if (!node) return;
    try {
      node.stop();
    } catch {
      /* not started yet */
    }
    node.disconnect();
    this.nodes.delete(id);
  }
}

/** The audio of `chunks` (one reply's, in order) from sample `from` on, as one buffer. */
export function restOf(chunks: Int16Array[], from: number): Int16Array {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Int16Array(Math.max(0, total - from));
  let at = 0;
  let skip = from;
  for (const c of chunks) {
    if (skip >= c.length) {
      skip -= c.length;
      continue;
    }
    const part = c.subarray(skip);
    skip = 0;
    out.set(part, at);
    at += part.length;
  }
  return out;
}

/** Plays a MediaStream through a local WebRTC connection so the AEC sees it as far-end audio. */
export class Loopback {
  private readonly a = new RTCPeerConnection();
  private readonly b = new RTCPeerConnection();
  readonly audio = new Audio();

  constructor(stream: MediaStream, sinkId?: string) {
    this.a.onicecandidate = (e) => e.candidate && void this.b.addIceCandidate(e.candidate);
    this.b.onicecandidate = (e) => e.candidate && void this.a.addIceCandidate(e.candidate);
    this.b.ontrack = (e) => {
      this.audio.srcObject = e.streams[0];
      if (sinkId) void (this.audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }).setSinkId?.(sinkId);
      void this.audio.play();
    };
    stream.getTracks().forEach((t) => this.a.addTrack(t, stream));
    void this.negotiate();
  }

  private async negotiate(): Promise<void> {
    const offer = await this.a.createOffer();
    await this.a.setLocalDescription(offer);
    await this.b.setRemoteDescription(offer);
    const answer = await this.b.createAnswer();
    await this.b.setLocalDescription(answer);
    await this.a.setRemoteDescription(answer);
  }

  close(): void {
    this.a.close();
    this.b.close();
  }
}
